import { useEffect, useRef, useState } from "react";
import { Building2, MapPin, User } from "lucide-react";
import { Input } from "./ui/input";
import { api } from "../lib/api";

const SOURCE_LABEL = { agency: "Agency", account: "Account", past_order: "Past order" };

const ContactRow = ({ s }) => (
  <>
    <span className="mt-0.5 text-brand-teal shrink-0">
      {s.organization ? <Building2 className="h-3.5 w-3.5" /> : <User className="h-3.5 w-3.5" />}
    </span>
    <span className="min-w-0 flex-1">
      <span className="block font-bold text-[12.5px] text-brand-charcoal truncate">
        {s.organization || s.name || s.email}
      </span>
      <span className="block text-[11px] text-brand-charcoal/60 truncate">
        {[s.organization ? s.name : null, s.email].filter(Boolean).join(" · ")}
      </span>
    </span>
    <span className="text-[9px] font-bold uppercase tracking-[0.1em] text-brand-charcoal/45 shrink-0">
      {SOURCE_LABEL[s.source] || ""}
    </span>
  </>
);

const AddressRow = ({ s }) => (
  <>
    <MapPin className="mt-0.5 h-3.5 w-3.5 text-brand-teal shrink-0" />
    <span className="min-w-0 flex-1">
      <span className="block font-bold text-[12.5px] text-brand-charcoal truncate">
        {s.address}{s.address_line2 ? `, ${s.address_line2}` : ""}
      </span>
      <span className="block text-[11px] text-brand-charcoal/60 truncate">
        {[s.city, s.state, s.zip_code].filter(Boolean).join(" ")}
      </span>
    </span>
  </>
);

// Text input with type-ahead suggestions from past invoices / accounts.
export const SuggestInput = ({ value, onChange, onPick, kind = "contact", email, testid, className = "", ...rest }) => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    const q = (value || "").trim();
    if (!focused || q.length < 2) {
      setItems([]);
      return undefined;
    }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/admin/invoice-suggestions", { params: { q, kind, email: email || undefined } });
        setItems(data.items || []);
        setOpen(true);
      } catch {
        setItems([]);
      }
    }, 200);
    return () => clearTimeout(t);
  }, [value, kind, email, focused]);

  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const pick = (s) => {
    onPick(s);
    setOpen(false);
    setItems([]);
  };

  return (
    <div ref={wrapRef} className={`relative ${className}`}>
      <Input
        data-testid={testid}
        value={value}
        autoComplete="off"
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => { setFocused(true); setOpen(true); }}
        onBlur={() => setFocused(false)}
        onKeyDown={(e) => { if (e.key === "Escape" && open && items.length > 0) { e.preventDefault(); setOpen(false); } }}
        className="h-11 rounded-xl bg-brand-cream/40 border-black/10"
        {...rest}
      />
      {open && items.length > 0 && (
        <ul
          data-testid={`${testid}-suggestions`}
          className="absolute z-40 top-full mt-1 left-0 right-0 rounded-2xl bg-white shadow-xl border border-black/10 max-h-64 overflow-auto py-1"
        >
          {items.map((s, i) => (
            <li key={`${s.email || s.address}-${i}`}>
              <button
                type="button"
                data-testid={`${testid}-suggestion-${i}`}
                onMouseDown={(e) => { e.preventDefault(); pick(s); }}
                className="w-full text-left px-3.5 py-2 hover:bg-brand-cream/60 flex items-start gap-2.5"
              >
                {kind === "address" ? <AddressRow s={s} /> : <ContactRow s={s} />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default SuggestInput;
